import { createFileRoute, Link, useNavigate, useSearch } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import { z } from "zod";
import { useServerFn } from "@tanstack/react-start";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { AuthShell } from "./login";
import { completeSignup, getSignupSession } from "@/lib/billing.functions";
import { supabase } from "@/integrations/supabase/client";
import { CheckCircle2, Loader2 } from "lucide-react";

const Search = z.object({
  session_id: z.string().min(1),
});

export const Route = createFileRoute("/complete-signup")({
  validateSearch: (s) => Search.parse(s),
  component: CompleteSignupPage,
});

type SessionInfo = {
  email: string;
  plan_name: string;
  price: { amount: number; suffix: string };
  already_completed: boolean;
};

function CompleteSignupPage() {
  const { session_id } = useSearch({ from: "/complete-signup" });
  const navigate = useNavigate();
  const lookup = useServerFn(getSignupSession);
  const finish = useServerFn(completeSignup);

  const [info, setInfo] = useState<SessionInfo | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [fullName, setFullName] = useState("");
  const [password, setPassword] = useState("");
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await lookup({ data: { session_id } });
        if (cancelled) return;
        setInfo({
          email: res.email,
          plan_name: res.plan_name,
          price: res.price,
          already_completed: res.already_completed,
        });
      } catch (err) {
        if (cancelled) return;
        setLoadError(err instanceof Error ? err.message : "Could not load checkout session");
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [session_id, lookup]);

  const onSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!info) return;
    setSubmitting(true);
    try {
      await finish({ data: { session_id, full_name: fullName, password } });
      const { error: signInErr } = await supabase.auth.signInWithPassword({
        email: info.email,
        password,
      });
      if (signInErr) throw signInErr;
      toast.success("Account created, welcome!");
      navigate({ to: "/onboarding" });
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Could not complete signup");
      setSubmitting(false);
    }
  };

  if (loadError) {
    return (
      <AuthShell title="Hmm, something's off" subtitle="We couldn't load your checkout session">
        <p className="text-sm text-muted-foreground">{loadError}</p>
        <Button asChild className="mt-4 w-full">
          <Link to="/signup">Back to signup</Link>
        </Button>
      </AuthShell>
    );
  }

  if (!info) {
    return (
      <AuthShell title="Finalizing your payment…" subtitle="One moment">
        <div className="flex items-center justify-center py-8">
          <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
        </div>
      </AuthShell>
    );
  }

  if (info.already_completed) {
    return (
      <AuthShell title="You're already signed up" subtitle="Sign in to access your dashboard">
        <p className="text-sm text-muted-foreground">
          An account for <span className="font-medium text-foreground">{info.email}</span> already
          exists. Please sign in with your password.
        </p>
        <Button asChild className="mt-4 w-full">
          <Link to="/login">Go to sign in</Link>
        </Button>
      </AuthShell>
    );
  }

  return (
    <AuthShell title="Set up your login" subtitle="Last step, create a password">
      <form onSubmit={onSubmit} className="space-y-5">
        <ol className="flex items-center gap-2 text-xs text-muted-foreground">
          <li className="flex items-center gap-1.5">
            <span className="flex h-5 w-5 items-center justify-center rounded-full bg-emerald-500/15 text-emerald-500">
              <CheckCircle2 className="h-3.5 w-3.5" />
            </span>
            Plan &amp; card
          </li>
          <li className="h-px flex-1 bg-border" />
          <li className="flex items-center gap-1.5 font-medium text-foreground">
            <span className="flex h-5 w-5 items-center justify-center rounded-full bg-primary text-[10px] font-semibold text-primary-foreground">
              2
            </span>
            Create login
          </li>
        </ol>

        <div className="rounded-lg border border-border bg-muted/30 p-3 text-xs">
          <div className="flex items-center justify-between">
            <span className="text-muted-foreground">Plan</span>
            <span className="font-medium text-foreground">{info.plan_name}</span>
          </div>
          <div className="mt-1 flex items-center justify-between">
            <span className="text-muted-foreground">Price</span>
            <span className="font-medium text-foreground">
              ${info.price.amount}
              <span className="text-muted-foreground">{info.price.suffix}</span> after trial
            </span>
          </div>
          <div className="mt-1 flex items-center justify-between">
            <span className="text-muted-foreground">Email</span>
            <span className="font-medium text-foreground">{info.email}</span>
          </div>
        </div>

        <div className="space-y-2">
          <Label htmlFor="name">Full name</Label>
          <Input
            id="name"
            required
            value={fullName}
            onChange={(e) => setFullName(e.target.value)}
            autoComplete="name"
          />
        </div>
        <div className="space-y-2">
          <Label htmlFor="password">Password</Label>
          <Input
            id="password"
            type="password"
            minLength={8}
            required
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            autoComplete="new-password"
          />
          <p className="text-xs text-muted-foreground">At least 8 characters.</p>
        </div>

        <Button type="submit" className="w-full" disabled={submitting}>
          {submitting ? "Creating your account…" : "Create account & sign in"}
        </Button>
      </form>
    </AuthShell>
  );
}
